import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import JourneyV2Details from 'components/Flights/V2/FareFamily/JourneyV2Details'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TextButton from 'LuxKit/Button/TextButton'
import { CurrencyProvider } from 'contexts/currencyContext'
import type { TransportOption } from 'tripPlanner/api/types'

export interface ShortlistedFlightDetailsSection {
  key: string
  journey: App.JourneyV2
}

interface Props {
  option: TransportOption
  sections: Array<ShortlistedFlightDetailsSection>
  price?: number
  currencyCode?: string
  onBookNow?: (option: TransportOption) => void
}

const DrawerContent = styled(Group)`
  width: 100%;
  max-width: ${rem(700)};
  margin: ${rem(24)} auto;
  padding: 0 ${rem(24)};
  box-sizing: border-box;
`

interface SectionProps {
  section: ShortlistedFlightDetailsSection
}

function Section({ section }: SectionProps) {
  const [fareRulesURL, setFareRulesURL] = useState<string | undefined>(undefined)
  const [fareRulesParams, setFareRulesParams] = useState<any>(undefined)

  return (
    <JourneyV2Details
      journey={section.journey}
      fareRulesURL={fareRulesURL}
      fareRulesParams={fareRulesParams}
      setFareRulesURL={setFareRulesURL}
      setFareRulesParams={setFareRulesParams}
    />
  )
}

function ShortlistedFlightDetailsDrawer({ option, sections, price, currencyCode, onBookNow }: Props) {
  const modalElement = useModalElementContext()
  const intl = useIntl()

  const handleClose = () => {
    modalElement.resolve()
  }

  const handleAfterClose = () => {
    modalElement.afterClose()
  }

  const handleBookNow = () => {
    modalElement.resolve()
    onBookNow?.(option)
  }

  const economyFromLabel = intl.formatMessage({
    id: 'qqmYB_u5YxspNNLr2yWr',
    defaultMessage: 'Economy from',
    description: 'Price caption shown above non-booked shortlisted flight price in the flight details drawer',
  })

  const bookNowLabel = intl.formatMessage({
    id: '4VTNHzs2wLQP5XngJurI',
    defaultMessage: 'Book now',
    description: 'Button label to book a shortlisted flight in the trip planner itinerary',
  })

  const content = useMemo(() => {
    if (sections.length <= 1) {
      return sections.map((section) => <Section key={section.key} section={section} />)
    }

    return sections.flatMap((section, index) => {
      const node = <Section key={section.key} section={section} />
      if (index === 0) {
        return [node, <Divider key={`${section.key}-divider`} kind="primary" />]
      }
      return [node]
    })
  }, [sections])

  const showPrice = price != null && !!currencyCode
  const showPricingRow = showPrice || !!onBookNow

  return (
    <TripDrawer
      isOpen={Boolean(modalElement.open)}
      onClose={handleClose}
      onAfterClose={handleAfterClose}
      direction="right-to-left"
    >
      <DrawerContent direction="vertical" gap={24}>
        {showPricingRow && (
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
            <Group direction="vertical" gap={4}>
              {showPrice && (
                <CurrencyProvider currency={currencyCode}>
                  <PriceRowPriceCaption>{economyFromLabel}</PriceRowPriceCaption>
                  <PriceRowPrice
                    size="L"
                    price={price}
                    saleUnit="adult"
                  />
                </CurrencyProvider>
              )}
            </Group>
            {onBookNow && (
              <TextButton kind="primary" size="large" onClick={handleBookNow}>
                {bookNowLabel}
              </TextButton>
            )}
          </Group>
        )}
        {content}
      </DrawerContent>
    </TripDrawer>
  )
}

export default ShortlistedFlightDetailsDrawer
